import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import type { SurveyComponent, SurveyQuestion, SurveySection } from '../../../types/builder';

interface SurveySettingsProps {
  component: SurveyComponent;
  onUpdate: (updates: Partial<SurveyComponent['settings']>) => void;
}

const QUESTION_TYPE_OPTIONS: SurveyQuestion['type'][] = [
  'multipleChoice',
  'checkbox',
  'dropdown',
  'text',
  'textarea',
  'date',
  'rating',
  'yesNo',
];

const VALIDATION_TYPE_OPTIONS = ['none', 'email', 'phone', 'number', 'url'] as const;

export const SurveySettings: React.FC<SurveySettingsProps> = ({ component, onUpdate }) => {
  const { t } = useTranslation('builder');
  const { settings } = component;
  const [draggedItem, setDraggedItem] = useState<{ type: 'question' | 'section'; sectionId?: string; questionId?: string } | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  // Helper: Get all questions (whether in sections or legacy format)
  const getAllQuestions = (): SurveyQuestion[] => {
    if (settings.enableSections && settings.sections) {
      return settings.sections.flatMap(s => s.questions);
    }
    return settings.questions || [];
  };

  const handleUpdate = (updates: Partial<SurveyComponent['settings']>) => {
    onUpdate(updates);
  };

  const handleToggleSections = (enabled: boolean) => {
    if (enabled) {
      // Convert legacy questions to sections
      const defaultSection: SurveySection = {
        id: crypto.randomUUID(),
        title: t('settings.survey.defaultSectionName', { n: 1 }),
        description: '',
        questions: settings.questions || [],
      };
      handleUpdate({ enableSections: true, sections: [defaultSection] });
    } else {
      // Convert sections back to flat questions
      const allQuestions = settings.sections?.flatMap(s => s.questions) || [];
      handleUpdate({ enableSections: false, sections: undefined, questions: allQuestions });
    }
  };

  // Section Management
  const handleAddSection = () => {
    const newSection: SurveySection = {
      id: crypto.randomUUID(),
      title: t('settings.survey.defaultSectionName', { n: (settings.sections?.length || 0) + 1 }),
      description: '',
      questions: [],
    };
    handleUpdate({ sections: [...(settings.sections || []), newSection] });
  };

  const handleUpdateSection = (sectionId: string, updates: Partial<SurveySection>) => {
    const updatedSections = settings.sections?.map(s =>
      s.id === sectionId ? { ...s, ...updates } : s
    );
    handleUpdate({ sections: updatedSections });
  };

  const handleDeleteSection = (sectionId: string) => {
    handleUpdate({ sections: settings.sections?.filter(s => s.id !== sectionId) });
  };

  // Question Management
  const handleAddQuestion = (sectionId?: string) => {
    const newQuestion: SurveyQuestion = {
      id: crypto.randomUUID(),
      type: 'text',
      question: t('settings.survey.newQuestionText'),
      required: false,
    };

    if (settings.enableSections && sectionId) {
      // Add to specific section
      const updatedSections = settings.sections?.map(s =>
        s.id === sectionId ? { ...s, questions: [...s.questions, newQuestion] } : s
      );
      handleUpdate({ sections: updatedSections });
    } else {
      // Add to legacy questions
      handleUpdate({ questions: [...(settings.questions || []), newQuestion] });
    }
  };

  const handleUpdateQuestion = (questionId: string, updates: Partial<SurveyQuestion>, sectionId?: string) => {
    if (settings.enableSections && sectionId && settings.sections) {
      const updatedSections = settings.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              questions: section.questions.map(q =>
                q.id === questionId ? { ...q, ...updates } : q
              ),
            }
          : section
      );
      handleUpdate({ sections: updatedSections });
    } else {
      const updatedQuestions = settings.questions?.map(q =>
        q.id === questionId ? { ...q, ...updates } : q
      );
      handleUpdate({ questions: updatedQuestions });
    }
  };

  const handleDeleteQuestion = (questionId: string, sectionId?: string) => {
    if (settings.enableSections && sectionId && settings.sections) {
      const updatedSections = settings.sections.map(section =>
        section.id === sectionId
          ? { ...section, questions: section.questions.filter(q => q.id !== questionId) }
          : section
      );
      handleUpdate({ sections: updatedSections });
    } else {
      handleUpdate({ questions: settings.questions?.filter(q => q.id !== questionId) });
    }
  };

  // Drag & Drop for Questions
  const handleDragStart = (e: React.DragEvent, questionId: string, sectionId?: string) => {
    setDraggedItem({ type: 'question', questionId, sectionId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetQuestionId: string, targetSectionId?: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== 'question' || !draggedItem.questionId) return;

    const sourceQuestionId = draggedItem.questionId;
    const sourceSectionId = draggedItem.sectionId;

    if (settings.enableSections && settings.sections) {
      // Multi-section mode
      let updatedSections = [...settings.sections];
      let draggedQuestion: SurveyQuestion | null = null;

      // Remove from source
      updatedSections = updatedSections.map(section => {
        if (section.id === sourceSectionId) {
          const question = section.questions.find(q => q.id === sourceQuestionId);
          if (question) draggedQuestion = question;
          return { ...section, questions: section.questions.filter(q => q.id !== sourceQuestionId) };
        }
        return section;
      });

      if (!draggedQuestion) return;

      // Insert at target position
      updatedSections = updatedSections.map(section => {
        if (section.id === targetSectionId) {
          const targetIndex = section.questions.findIndex(q => q.id === targetQuestionId);
          const newQuestions = [...section.questions];
          newQuestions.splice(targetIndex, 0, draggedQuestion!);
          return { ...section, questions: newQuestions };
        }
        return section;
      });

      handleUpdate({ sections: updatedSections });
    } else {
      // Single-page mode
      const questions = [...(settings.questions || [])];
      const sourceIndex = questions.findIndex(q => q.id === sourceQuestionId);
      const targetIndex = questions.findIndex(q => q.id === targetQuestionId);

      if (sourceIndex === -1 || targetIndex === -1) return;

      const [removed] = questions.splice(sourceIndex, 1);
      questions.splice(targetIndex, 0, removed);

      handleUpdate({ questions });
    }

    setDraggedItem(null);
  };

  // Options management
  const handleAddOption = (questionId: string, sectionId?: string) => {
    const question = getAllQuestions().find(q => q.id === questionId);
    if (!question) return;

    const newOptions = [
      ...(question.options || []),
      t('settings.survey.newOption', { n: (question.options?.length || 0) + 1 }),
    ];
    handleUpdateQuestion(questionId, { options: newOptions }, sectionId);
  };

  const handleUpdateOption = (questionId: string, optionIndex: number, value: string, sectionId?: string) => {
    const question = getAllQuestions().find(q => q.id === questionId);
    if (!question || !question.options) return;

    const newOptions = [...question.options];
    newOptions[optionIndex] = value;
    handleUpdateQuestion(questionId, { options: newOptions }, sectionId);
  };

  const handleDeleteOption = (questionId: string, optionIndex: number, sectionId?: string) => {
    const question = getAllQuestions().find(q => q.id === questionId);
    if (!question || !question.options) return;

    const newOptions = question.options.filter((_, i) => i !== optionIndex);
    handleUpdateQuestion(questionId, { options: newOptions }, sectionId);
  };

  const toggleSectionExpand = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
    );
  };

  const renderQuestion = (question: SurveyQuestion, sectionId?: string) => (
    <div
      key={question.id}
      draggable
      onDragStart={(e) => handleDragStart(e, question.id, sectionId)}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, question.id, sectionId)}
      className="bg-gray-50 border border-gray-200 rounded-lg p-4 cursor-move hover:border-blue-400 transition-colors"
    >
      {/* Question Header */}
      <div className="flex items-start gap-2 mb-3">
        <GripVertical size={20} className="text-gray-400 mt-1 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={question.question}
            onChange={(e) => handleUpdateQuestion(question.id, { question: e.target.value }, sectionId)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('settings.survey.questionTextPlaceholder')}
          />
          <input
            type="text"
            value={question.description || ''}
            onChange={(e) => handleUpdateQuestion(question.id, { description: e.target.value }, sectionId)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
            placeholder={t('settings.survey.questionDescriptionPlaceholder')}
          />
        </div>
        <button
          onClick={() => handleDeleteQuestion(question.id, sectionId)}
          className="text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* Question Type */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('settings.survey.questionType')}</label>
        <select
          value={question.type}
          onChange={(e) => {
            const defaultOptions = [
              t('settings.survey.defaultOptionA'),
              t('settings.survey.defaultOptionB'),
            ];
            handleUpdateQuestion(question.id, {
              type: e.target.value as SurveyQuestion['type'],
              ...(e.target.value === 'rating' && { maxRating: 5, options: undefined }),
              ...(e.target.value === 'multipleChoice' && { options: defaultOptions, maxRating: undefined }),
              ...(e.target.value === 'checkbox' && { options: defaultOptions, maxRating: undefined }),
              ...(e.target.value === 'dropdown' && { options: defaultOptions, maxRating: undefined }),
              ...(['text', 'textarea', 'yesNo', 'date'].includes(e.target.value) && { options: undefined, maxRating: undefined }),
            }, sectionId);
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {QUESTION_TYPE_OPTIONS.map((key) => (
            <option key={key} value={key}>
              {t(`settings.survey.questionTypes.${key}` as const)}
            </option>
          ))}
        </select>
      </div>

      {/* Options */}
      {(question.type === 'multipleChoice' || question.type === 'checkbox' || question.type === 'dropdown') && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-2">{t('settings.survey.options')}</label>
          <div className="space-y-2">
            {question.options?.map((option, optionIndex) => (
              <div key={optionIndex} className="flex items-center gap-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => handleUpdateOption(question.id, optionIndex, e.target.value, sectionId)}
                  className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => handleDeleteOption(question.id, optionIndex, sectionId)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button
              onClick={() => handleAddOption(question.id, sectionId)}
              className="text-blue-600 text-sm hover:text-blue-700 transition-colors"
            >
              {t('settings.survey.addOption')}
            </button>
          </div>
        </div>
      )}

      {/* Max Rating */}
      {question.type === 'rating' && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('settings.survey.maxRating')}</label>
          <select
            value={question.maxRating || 5}
            onChange={(e) => handleUpdateQuestion(question.id, { maxRating: parseInt(e.target.value) }, sectionId)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="5">{t('settings.survey.maxRating5')}</option>
            <option value="10">{t('settings.survey.maxRating10')}</option>
          </select>
        </div>
      )}

      {/* Validation */}
      {(question.type === 'text' || question.type === 'textarea') && (
        <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <label className="block text-xs font-semibold text-blue-900 mb-2">{t('settings.survey.validationHeading')}</label>
          <div className="mb-2">
            <select
              value={question.validation?.type || 'none'}
              onChange={(e) => handleUpdateQuestion(question.id, {
                validation: { ...question.validation, type: e.target.value as any }
              }, sectionId)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900"
            >
              {VALIDATION_TYPE_OPTIONS.map((key) => (
                <option key={key} value={key}>
                  {t(`settings.survey.validationTypes.${key}` as const)}
                </option>
              ))}
            </select>
          </div>
          {question.validation?.type === 'number' && (
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                type="number"
                placeholder={t('settings.survey.min')}
                value={question.validation?.min || ''}
                onChange={(e) => handleUpdateQuestion(question.id, {
                  validation: { ...question.validation, min: e.target.value ? parseInt(e.target.value) : undefined }
                }, sectionId)}
                className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-900"
              />
              <input
                type="number"
                placeholder={t('settings.survey.max')}
                value={question.validation?.max || ''}
                onChange={(e) => handleUpdateQuestion(question.id, {
                  validation: { ...question.validation, max: e.target.value ? parseInt(e.target.value) : undefined }
                }, sectionId)}
                className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-900"
              />
            </div>
          )}
          {question.validation?.type && question.validation.type !== 'none' && (
            <input
              type="text"
              placeholder={t('settings.survey.customErrorMessage')}
              value={question.validation?.errorMessage || ''}
              onChange={(e) => handleUpdateQuestion(question.id, {
                validation: { ...question.validation, errorMessage: e.target.value }
              }, sectionId)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900"
            />
          )}
        </div>
      )}

      {/* Required Toggle */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id={`required-${question.id}`}
          checked={question.required}
          onChange={(e) => handleUpdateQuestion(question.id, { required: e.target.checked }, sectionId)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded"
        />
        <label htmlFor={`required-${question.id}`} className="text-sm text-gray-700">{t('settings.survey.requiredQuestion')}</label>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Survey Title & Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.survey.surveyTitle')}</label>
        <input
          type="text"
          value={settings.title}
          onChange={(e) => handleUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.survey.description')}</label>
        <textarea
          value={settings.description}
          onChange={(e) => handleUpdate({ description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900"
          rows={2}
        />
      </div>

      {/* Feature Toggles */}
      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Layers size={16} className="text-purple-600" />
              {t('settings.survey.multiPageHeading')}
            </p>
            <p className="text-xs text-gray-600">{t('settings.survey.multiPageHint')}</p>
          </div>
          <button
            onClick={() => handleToggleSections(!settings.enableSections)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.enableSections ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.enableSections ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {settings.enableSections && (
          <div className="flex items-center justify-between pt-2 border-t border-purple-200">
            <div>
              <p className="text-sm font-semibold text-gray-900">{t('settings.survey.progressBar')}</p>
              <p className="text-xs text-gray-600">{t('settings.survey.progressBarHint')}</p>
            </div>
            <button
              onClick={() => handleUpdate({ showProgressBar: !settings.showProgressBar })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.showProgressBar ? 'bg-purple-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.showProgressBar ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}
      </div>

      {/* Questions/Sections */}
      {settings.enableSections ? (
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">{t('settings.survey.sections')}</label>
            <button
              onClick={handleAddSection}
              className="flex items-center gap-1 px-3 py-1 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700"
            >
              <Plus size={16} />
              {t('settings.survey.addSection')}
            </button>
          </div>

          <div className="space-y-4">
            {settings.sections?.map((section) => {
              const isExpanded = expandedSections.includes(section.id);
              return (
                <div key={section.id} className="bg-white border-2 border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => handleUpdateSection(section.id, { title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-900"
                        placeholder={t('settings.survey.sectionTitlePlaceholder')}
                      />
                      <input
                        type="text"
                        value={section.description || ''}
                        onChange={(e) => handleUpdateSection(section.id, { description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-600 mt-2"
                        placeholder={t('settings.survey.sectionDescriptionPlaceholder')}
                      />
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <button
                        onClick={() => toggleSectionExpand(section.id)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      <button
                        onClick={() => handleDeleteSection(section.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <>
                      <div className="space-y-3 mb-3">
                        {section.questions.map((question) => renderQuestion(question, section.id))}
                      </div>
                      <button
                        onClick={() => handleAddQuestion(section.id)}
                        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-purple-400 hover:text-purple-600 transition-colors"
                      >
                        {t('settings.survey.addQuestionTo', { section: section.title })}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">{t('settings.survey.questions')}</label>
            <button
              onClick={() => handleAddQuestion()}
              className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              <Plus size={16} />
              {t('settings.survey.addQuestion')}
            </button>
          </div>

          <div className="space-y-4">
            {settings.questions?.map((question) => renderQuestion(question))}
          </div>
        </div>
      )}

      {/* Submit Button Settings */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.common.submitButtonText')}</label>
        <input
          type="text"
          value={settings.submitButtonText}
          onChange={(e) => handleUpdate({ submitButtonText: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.common.submitButtonColor')}</label>
        <input
          type="color"
          value={settings.submitButtonColor}
          onChange={(e) => handleUpdate({ submitButtonColor: e.target.value })}
          className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="showResults"
          checked={settings.showResults}
          onChange={(e) => handleUpdate({ showResults: e.target.checked })}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded"
        />
        <label htmlFor="showResults" className="text-sm text-gray-700">{t('settings.survey.showResults')}</label>
      </div>

      {/* Colors */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.common.backgroundColor')}</label>
          <input
            type="color"
            value={settings.backgroundColor || 'transparent'}
            onChange={(e) => handleUpdate({ backgroundColor: e.target.value })}
            className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('settings.common.textColor')}</label>
          <input
            type="color"
            value={settings.textColor}
            onChange={(e) => handleUpdate({ textColor: e.target.value })}
            className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
          />
        </div>
      </div>

      {/* Padding */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.paddingPx', { value: settings.padding })}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
};
